import type { VideoFrame } from '../types';
import { convertFrame, convertRGB565, convertXRGB8888, pack } from './convert';

export interface RendererOptions {
  /** ドットをぼかさない（レトロ機は基本 true） */
  pixelPerfect?: boolean;
  /** 表示アスペクト比。ネイティブ解像度と異なる場合に指定（NES/SNES）。 */
  aspect?: number;
  /**
   * 縦方向を整数倍に丸める。
   * 半端な倍率だと走査線ごとに太さが変わり、スクロール時にちらつく。
   * 画面を目一杯使えなくなる代わりに見た目がかなり安定する。
   */
  integerScale?: boolean;
}

/**
 * Canvas 2D ベースのフレームレンダラ。
 *
 * 内部 canvas はコアのネイティブ解像度そのままで持ち、拡大は CSS に任せる。
 * こうすると putImageData が最小コストで済み、iPhone の GPU 合成に乗るので
 * ScriptProcessor と競合してもフレーム落ちしにくい。
 */
export class CanvasRenderer {
  readonly canvas: HTMLCanvasElement;
  private ctx: CanvasRenderingContext2D;
  private image: ImageData | null = null;
  private rgba: Uint32Array | null = null;
  private width = 0;
  private height = 0;
  private opts: RendererOptions;

  private container: HTMLElement | null = null;
  private resizeObserver: ResizeObserver | null = null;
  private displayAspect: number | null = null;

  constructor(opts: RendererOptions = {}) {
    this.opts = { pixelPerfect: true, ...opts };
    this.canvas = document.createElement('canvas');
    this.canvas.width = 1;
    this.canvas.height = 1;

    const ctx = this.canvas.getContext('2d', {
      alpha: false,
      desynchronized: true,
      willReadFrequently: false,
    });
    if (!ctx) throw new Error('2D canvas context is unavailable');
    this.ctx = ctx;
    this.ctx.imageSmoothingEnabled = !this.opts.pixelPerfect;

    this.applyStyle();
  }

  private applyStyle(): void {
    const s = this.canvas.style;
    s.display = 'block';
    s.backgroundColor = '#000';
    s.imageRendering = this.opts.pixelPerfect ? 'pixelated' : 'auto';
    // 画面ドラッグでスクロールやテキスト選択が起きないように
    s.touchAction = 'none';
    s.userSelect = 'none';
    (s as CSSStyleDeclaration & { webkitUserSelect?: string }).webkitUserSelect = 'none';
  }

  attachTo(container: HTMLElement): void {
    this.container = container;
    container.appendChild(this.canvas);

    // 表示サイズは CSS ではなく JS で決める。
    //
    // width:100% / height:100% / aspect-ratio / object-fit を組み合わせると、
    // 「幅で決まる高さ」と「max-height による制限」が食い違ったときに
    // 縦横比が崩れたり、コンテナからはみ出したりする。横持ちで顕著に壊れる。
    // 収まる最大サイズを自分で計算して px で指定する方が確実で、
    // 整数倍スケーリングの実装もここに足せる。
    this.resizeObserver = new ResizeObserver(() => this.layout());
    this.resizeObserver.observe(container);
    this.layout();
  }

  /** 表示上の縦横比を指定する（NES/SNES は非正方ピクセルなので実解像度と異なる）。 */
  setDisplayAspect(aspect: number): void {
    if (this.displayAspect === aspect) return;
    this.displayAspect = aspect;
    this.layout();
  }

  /** コンテナに収まる最大サイズを縦横比を保って計算し、px で適用する。 */
  private layout(): void {
    const container = this.container;
    if (!container) return;

    const style = getComputedStyle(container);
    const availW = container.clientWidth - parseFloat(style.paddingLeft) - parseFloat(style.paddingRight);
    const availH = container.clientHeight - parseFloat(style.paddingTop) - parseFloat(style.paddingBottom);
    if (availW <= 0 || availH <= 0) return;

    const aspect = this.displayAspect ?? this.opts.aspect ?? (this.width || 4) / (this.height || 3);

    // 幅・高さのどちらが先に頭打ちになるかで決める
    let w = availW;
    let h = w / aspect;
    if (h > availH) {
      h = availH;
      w = h * aspect;
    }

    // 整数倍スケーリング: 縦を native の整数倍に切り下げ、幅は比率から再計算する。
    // 横も整数倍にすると非正方ピクセルの機種で縦横比が狂うので縦だけに掛ける。
    if (this.opts.integerScale && this.height > 0) {
      const scale = Math.max(1, Math.floor(h / this.height));
      const snappedH = this.height * scale;
      if (snappedH <= availH) {
        h = snappedH;
        w = h * aspect;
      }
    }

    this.canvas.style.width = `${Math.floor(w)}px`;
    this.canvas.style.height = `${Math.floor(h)}px`;
  }

  setIntegerScale(on: boolean): void {
    if (this.opts.integerScale === on) return;
    this.opts.integerScale = on;
    this.layout();
  }

  private ensureSize(w: number, h: number): void {
    if (this.width === w && this.height === h) return;
    this.width = w;
    this.height = h;
    this.canvas.width = w;
    this.canvas.height = h;
    this.image = this.ctx.createImageData(w, h);
    this.rgba = new Uint32Array(this.image.data.buffer);
    this.ctx.imageSmoothingEnabled = !this.opts.pixelPerfect;
    this.layout();
  }

  draw(frame: VideoFrame): void {
    this.ensureSize(frame.width, frame.height);
    const img = this.image;
    const out = this.rgba;
    if (!img || !out) return;

    convertFrame(frame, out, img.data);
    this.ctx.putImageData(img, 0, 0);
  }

  clear(): void {
    this.ctx.fillStyle = '#000';
    this.ctx.fillRect(0, 0, this.canvas.width, this.canvas.height);
  }

  async toBlob(type = 'image/png'): Promise<Blob | null> {
    return new Promise((resolve) => {
      this.canvas.toBlob((b) => resolve(b), type);
    });
  }

  dispose(): void {
    this.resizeObserver?.disconnect();
    this.resizeObserver = null;
    this.container = null;
    this.canvas.remove();
    this.image = null;
    this.rgba = null;
  }
}

export const __test__ = { convertRGB565, convertXRGB8888, pack };
